import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as R from 'ramda'
import { type Field, RoleName } from '@pbt/pbt-ui-components'

import { fetchTeamsList } from '~/store/actions/members'
import {
  getCurrentBusinessId,
  getCurrentBusinessIsOmniChannel,
} from '~/store/reducers/auth'
import { getMemberIsLoading } from '~/store/reducers/members'
import { getRoleIdByName } from '~/store/reducers/roles'
import { getTeamByRoleIdAndBusinessId } from '~/store/reducers/users'
import type { Task } from '~/types'
import { isNilOrEmpty } from '~/utils'

const isBothNilOrEqual = (a: any, b: any) =>
  (isNilOrEmpty(a) && isNilOrEmpty(b)) || R.equals(a, b)
const isDatesBothNilOrEqual = (a: any, b: any) =>
  (isNilOrEmpty(a) && isNilOrEmpty(b)) ||
  new Date(a).getTime() === new Date(b).getTime()

const TaskFields: (keyof Task)[] = [
  'assigneeId',
  'clientId',
  'patientId',
  'contactId',
  'appointmentId',
  'recurrencePeriod',
  'instructions',
  'name',
  'typeId',
  'notes',
  'recurrenceModeId',
  'occurrencesCount',
]

const TaskDateFields: (keyof Task)[] = ['dueDate', 'recurrenceEndDatetime']

const isAssigneeEqual = (initialTask: Task, newTask: Task) => {
  // because of the denormalization and different prop names we need to check both of them
  const initialAssigned = initialTask.assigned || initialTask.assigneeId
  const newAssigned = newTask.assigned || newTask.assigneeId
  return isBothNilOrEqual(initialAssigned, newAssigned)
}

export const isSharedToPersonIdsEqual = (initialTask: Task, newTask: Task) => {
  const initialSharedToPersonIds = initialTask.sharedToPersonIds || []
  const newSharedToPersonIds = newTask.sharedToPersonIds || []
  return R.equals(
    R.sortBy((id: string) => id, initialSharedToPersonIds as string[]),
    R.sortBy((id: string) => id, newSharedToPersonIds as string[]),
  )
}

export const getTaskChanges = (initialTask: Task, newTask: Task) => {
  const getPropsWithChanges = [
    ...TaskDateFields.filter(
      (key) => !isDatesBothNilOrEqual(initialTask[key], newTask[key]),
    ),
    ...TaskFields.filter(
      (key) => !isBothNilOrEqual(initialTask[key], newTask[key]),
    ),
  ]

  const changes = R.pick(getPropsWithChanges, newTask)

  if (!isAssigneeEqual(initialTask, newTask)) {
    changes.assigneeId = newTask.assigneeId
  }

  if (!isSharedToPersonIdsEqual(initialTask, newTask)) {
    changes.sharedToPersonIds = newTask.sharedToPersonIds
  }

  if (R.has('appointmentId', changes)) {
    changes.soapId = null
    changes.orderId = null
    changes.orderType = null
  }

  return changes
}

export const useInitializeClientConciergeOnCreation = ({
  copyToIds,
  isCreate,
}: {
  copyToIds: Field
  isCreate: boolean
}) => {
  const dispatch = useDispatch()

  const isOmnichannel = useSelector(getCurrentBusinessIsOmniChannel)
  const businessId = useSelector(getCurrentBusinessId)
  const clientConciergeRoleId = useSelector(
    getRoleIdByName(RoleName.ClientConcierge),
  )
  const clientConcierge = useSelector(
    getTeamByRoleIdAndBusinessId(clientConciergeRoleId || '', businessId!),
  )
  const isLoadingMember = useSelector(getMemberIsLoading)

  useEffect(() => {
    if (isCreate) {
      dispatch(fetchTeamsList({ businessId }))
    }
  }, [isCreate, businessId])

  useEffect(() => {
    if (isCreate && isOmnichannel && clientConcierge?.id) {
      copyToIds.setValue([clientConcierge.id])
    }
  }, [clientConcierge?.id, isCreate, isOmnichannel])

  return {
    isLoading: isOmnichannel ? isLoadingMember : false,
  }
}
